import { enableProdMode, ErrorHandler, importProvidersFrom } from '@angular/core';
import 'hammerjs';

import { environment } from './environments/environment';
import { AppComponent } from './app/app.component';
import { SharedModule } from './app/shared/shared.module';
import { AppRoutingModule } from './app/app-routing.module';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import { RouteReuseStrategy } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import * as Sentry from '@sentry/angular';
import { provideServiceWorker } from '@angular/service-worker';
import { ApiUrlInterceptor } from './app/core/interceptors/api-url.interceptor';
import { ChunkErrorHandler } from './app/core/services/error/chunk-error-handler';
import { AuthFacade } from './app/core/facades/auth.facade';
import { AuthFacadeImpl } from './app/core/facades/auth.facade.impl';
import { provideRoleUserModals } from './app/role-user/providers/role-user-modals.provider';

Sentry.init({
  dsn: 'https://d4fe4c57e0303ae7491551f3f633f89d@o4507828218036224.ingest.us.sentry.io/4507830617636864',
  enabled: environment.production,

  integrations: [
    // Registers and configures the Tracing integration,
    // which automatically instruments your application to monitor its
    // performance, including custom Angular routing instrumentation
    Sentry.browserTracingIntegration(),
    // Registers the Replay integration,
    // which automatically captures Session Replays
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      // need to mask sensitive data
      // mask: [".*password.*", ".*credit-card.*"],
    }),
  ],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for tracing.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,

  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/dev\.api\.sunnystreet\.com\/api/,
    /^https:\/\/api\.sunnystreet\.com\/api/,
  ],

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(BrowserModule, AppRoutingModule, SharedModule),
    provideAnimations(),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideHttpClient(withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: ApiUrlInterceptor, multi: true },
    { provide: ErrorHandler, useClass: ChunkErrorHandler },
    { provide: AuthFacade, useClass: AuthFacadeImpl },
    provideRoleUserModals(),
    provideIonicAngular({
      mode: 'ios', // 'ios' for ios, 'md' for android
      // useSetInputAPI: true enables Angular's ComponentRef.setInput() for modal componentProps.
      // Required for signal inputs (input.required<T>()) and enforces @Input() decorators on all
      // modal components. This is the recommended approach for Angular 18+ apps using Ionic 8.
      // See: https://ionicframework.com/docs/angular/config
      useSetInputAPI: true,
    }),
    // sw-custom.js is push-only (no caching) — ngsw re-integration tracked in SUN-814
    provideServiceWorker('sw-custom.js', {
      enabled: environment.enableServiceWorker ?? environment.production,
      registrationStrategy: 'registerWhenStable:5000',
    }),
  ],
}).catch(err => {
  console.error('Bootstrap error:', err);
  Sentry.captureException(err);
});